import { patchAdminUser } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { IAdminUserDetail, UserRole } from '@/entities/admin-user.entity';
import { KeyRoundIcon, ShieldIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface ManageTabProps {
	user: IAdminUserDetail;
	onUpdated: (user: IAdminUserDetail) => void;
}

export default function ManageTab({ user, onUpdated }: ManageTabProps) {
	const [role, setRole] = useState<UserRole>(user.role);
	const [isBanned, setIsBanned] = useState(user.isBanned);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [newPin, setNewPin] = useState('');
	const [pinConfirmOpen, setPinConfirmOpen] = useState(false);
	const [isSavingPin, setIsSavingPin] = useState(false);

	useEffect(() => {
		setRole(user.role);
		setIsBanned(user.isBanned);
	}, [user]);

	const hasChanges = role !== user.role || isBanned !== user.isBanned;

	async function handleSave() {
		setIsSaving(true);
		try {
			const updated = await patchAdminUser(user.id, {
				role: role !== user.role ? role : undefined,
				isBanned: isBanned !== user.isBanned ? isBanned : undefined,
			});
			onUpdated(updated);
			toast.success('Изменения сохранены');
			setConfirmOpen(false);
		} finally {
			setIsSaving(false);
		}
	}

	async function handlePinSave() {
		setIsSavingPin(true);
		try {
			await patchAdminUser(user.id, { pin: newPin });
			toast.success('Пин-код изменён');
			setNewPin('');
			setPinConfirmOpen(false);
		} finally {
			setIsSavingPin(false);
		}
	}

	return (
		<>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-muted/60 p-2'>
							<ShieldIcon size={20} />
						</div>
						<CardTitle>Управление</CardTitle>
					</div>
				</CardHeader>
				<LoadingWrapper show={isSaving}>
					<CardContent className='px-4 py-4 flex flex-col gap-4'>
						<div className='flex flex-col gap-1.5'>
							<Label>Роль</Label>
							<Select value={role} onValueChange={v => setRole(v as UserRole)}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='regwait'>Ожидание</SelectItem>
									<SelectItem value='worker'>Worker</SelectItem>
									<SelectItem value='carder'>Carder</SelectItem>
									<SelectItem value='partner'>Partner</SelectItem>
									<SelectItem value='admin'>Admin</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label>Забанен</Label>
								<span className='text-xs text-muted-foreground'>Блокирует доступ к панели</span>
							</div>
							<Switch checked={isBanned} onCheckedChange={setIsBanned} />
						</div>

						<Button className='w-full' disabled={!hasChanges} onClick={() => setConfirmOpen(true)}>
							Сохранить
						</Button>
					</CardContent>
				</LoadingWrapper>
			</Card>

			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-muted/60 p-2'>
							<KeyRoundIcon size={20} />
						</div>
						<CardTitle>Пин-код</CardTitle>
					</div>
				</CardHeader>
				<LoadingWrapper show={isSavingPin}>
					<CardContent className='px-4 py-4 flex flex-col gap-4'>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='admin-new-pin'>Новый пин-код</Label>
							<span className='text-xs text-muted-foreground'>4 символа. Текущий пин не показывается.</span>
							<Input
								id='admin-new-pin'
								type='password'
								inputMode='numeric'
								maxLength={4}
								autoComplete='off'
								value={newPin}
								onChange={e => setNewPin(e.target.value)}
								disabled={isSavingPin}
							/>
						</div>
						<Button
							className='w-full'
							variant='secondary'
							disabled={newPin.length !== 4 || isSavingPin}
							onClick={() => setPinConfirmOpen(true)}
						>
							Сменить пин-код
						</Button>
					</CardContent>
				</LoadingWrapper>
			</Card>

			<Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Подтвердите изменения</DialogTitle>
					</DialogHeader>
					<div className='text-sm text-muted-foreground space-y-1'>
						{role !== user.role && (
							<p>
								Роль: <strong>{user.role}</strong> → <strong>{role}</strong>
							</p>
						)}
						{isBanned !== user.isBanned && (
							<p>
								Бан: <strong>{user.isBanned ? 'да' : 'нет'}</strong> → <strong>{isBanned ? 'да' : 'нет'}</strong>
							</p>
						)}
					</div>
					<DialogFooter>
						<Button variant='outline' onClick={() => setConfirmOpen(false)}>
							Отмена
						</Button>
						<Button onClick={handleSave} disabled={isSaving}>
							Подтвердить
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={pinConfirmOpen} onOpenChange={setPinConfirmOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Сменить пин-код</DialogTitle>
					</DialogHeader>
					<p className='text-sm text-muted-foreground'>
						Установить новый пин-код для <strong>#{user.nickname}</strong>? Активные socket-сессии пользователя будут
						отключены.
					</p>
					<DialogFooter>
						<Button variant='outline' onClick={() => setPinConfirmOpen(false)}>
							Отмена
						</Button>
						<Button onClick={handlePinSave} disabled={isSavingPin}>
							Подтвердить
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
